import type { ConfirmableItem, ConvertDocumentRequest, MessageCard, WorkflowRequest } from '~/types/web-chat'
import type { ConfirmId } from '~/composables/web-chat/dispatch'
import { TOOL_PRESENTATION, isToolId, type ToolId } from '#shared/tools/tool'

/**
 * Confirm tool call → the card it renders. PURE: no store, no fetch, no side effect.
 *
 * Split out of `useConfirmCards` (MAR-991) because a card is now built from TWO places: the live
 * `tool` wire event (`applyConfirmRequest`), and the persisted `tool_calls` row a reload reads back
 * (`confirmCardRehydration.ts`). One builder means a reloaded card cannot drift from the card the user
 * first saw. What a live card also DOES when it appears — attach a single search hit, resolve the
 * names the app owns — stays in `useConfirmCards`, because a rebuild must not re-attach anything.
 *
 * Exhaustive over `ConfirmId` (ids derived from the registry by kind), so adding a confirm tool
 * breaks the build until its card exists.
 */

/** What a card needs that is not in the tool call — only `clear_chat_scope`'s label, from the store. */
export interface CardBuildContext {
  scopeLabel: string
}

/**
 * The items a confirm tool proposes to act on.
 *
 * Reads `input.items` and nothing else. This used to guess across four shapes
 * (`items` / `document_ids` / `ids` / a bare `id`) because no payload contract existed; the
 * registry now says exactly what each tool sends, and every tool that carries items sends `items`
 * (`delete_files`, `move_files`, `file_to_case`, `download_documents`, `present_document_results`). A tool that
 * grows a different shape declares it in `shared/web-chat/tools.ts` and gets read here explicitly,
 * rather than being absorbed by a guess.
 */
export function normalizeConfirmableItems(input: Record<string, any>): ConfirmableItem[] {
  if (!Array.isArray(input.items)) return []
  return input.items
    .map((item: any) => ({
      id: String(item?.id || ''),
      name: String(item?.name || item?.file_name || item?.id || ''),
      type: item?.type ? String(item.type) : undefined,
    }))
    .filter((item: ConfirmableItem) => item.id)
}

export function buildConfirmCard(id: ConfirmId, input: Record<string, any>, callId: string | undefined, ctx: CardBuildContext): MessageCard {
  const builders: Record<ConfirmId, () => MessageCard> = {
    delete_files: () => ({
      toolId: 'delete_files',
      callId,
      request: { items: normalizeConfirmableItems(input), status: 'pending' },
    }),
    move_files: () => ({
      toolId: 'move_files',
      callId,
      request: {
        items: normalizeConfirmableItems(input),
        destination: String(input.destination || '/'),
        status: 'pending',
      },
    }),
    rename_files: () => ({
      toolId: 'rename_files',
      callId,
      request: {
        fileId: String(input.file_id || ''),
        currentName: String(input.current_name || ''),
        newName: String(input.new_name || ''),
        status: 'pending',
      },
    }),
    create_folder: () => ({
      toolId: 'create_folder',
      callId,
      request: {
        name: String(input.name || '').trim(),
        path: String(input.path || '/'),
        status: 'pending',
      },
    }),
    file_to_case: () => ({
      toolId: 'file_to_case',
      callId,
      request: {
        items: normalizeConfirmableItems(input).filter(item => item.type !== 'folder'),
        caseId: String(input.case_id || ''),
        // Replaced by the app's own name for `caseId` once it resolves; never shown as a guess.
        caseName: '',
        status: 'pending',
        loading: true,
      },
    }),
    clear_chat_scope: () => ({
      toolId: 'clear_chat_scope',
      callId,
      // The label comes from the store, not the model: the agent never sees the scope's ids and
      // shouldn't be trusted to restate its name on a card the user acts on.
      request: {
        scopeLabel: ctx.scopeLabel,
        reason: String(input.reason || ''),
        status: 'pending',
      },
    }),
    download_documents: () => ({
      toolId: 'download_documents',
      callId,
      request: {
        items: normalizeConfirmableItems(input).filter(item => item.type !== 'folder'),
        reason: String(input.reason || ''),
        downloaded: [],
      },
    }),
    run_workflow: () => {
      const request: WorkflowRequest = {
        workflowId: String(input.workflow_id || ''),
        // Replaced by the real name once the definition loads; never shown as a guess.
        workflowName: '',
        reason: String(input.reason || ''),
        status: 'pending',
        fields: [],
        loading: true,
        // Where an unconfirmed proposal is saved (MAR-988) — see `WorkflowRequest.proposalCallId`.
        ...(callId ? { proposalCallId: callId } : {}),
      }
      return { toolId: 'run_workflow', callId, request }
    },
    convert_document: () => {
      const toolId = (isToolId(input.tool_id) ? input.tool_id : 'image_to_word') as ToolId
      const presentation = TOOL_PRESENTATION[toolId]
      const request: ConvertDocumentRequest = {
        toolId,
        documentId: String(input.document_id || ''),
        // Known from code, so it is final on the first paint — only the filename is resolved.
        toolLabel: presentation.label,
        documentName: '',
        source: presentation.accepts,
        status: 'pending',
        loading: true,
      }
      return { toolId: 'convert_document', callId, request }
    },
    present_document_results: () => {
      const docs = normalizeConfirmableItems(input)
        .filter(item => item.type !== 'folder')
        .map(item => ({
          id: item.id,
          name: item.name,
          file_name: null,
          contract_type: (Array.isArray(input.items) ? input.items : [])
            .find((raw: any) => String(raw?.id ?? '') === item.id)?.contract_type ?? null,
        }))
      return {
        toolId: 'present_document_results',
        callId,
        request: { intro: String(input.reason || ''), docs },
      }
    },
  }
  return builders[id]()
}
